import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Flex,
  Text,
  Accordion,
  Code,
  useMantineTheme,
} from "@mantine/core";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";
import { useStylesTechnicalDetail } from "../utils/mantine/useStylesTechnicalDetail";
import { useStylesAccordion } from "../utils/mantine/useStylesAccordion";

/**
 * Parse error from backend - extract the structure.
 * @param {Object|string} errorData - Error data from backend or fallback format
 * @returns {Object} Parsed error with userMessage and technicalDetails
 */
const parseError = (errorData) => {
  // New format: Structured error from backend with user_message and technical_details
  if (errorData?.error?.user_message && errorData?.error?.technical_details) {
    return {
      userMessage: errorData.error.user_message,
      technicalDetails: errorData.error.technical_details,
    };
  }

  // Fallback: Display error as-is without inventing messages
  return {
    userMessage: "An error occurred. Please contact support.",
    technicalDetails: {
      message:
        typeof errorData === "string"
          ? errorData
          : JSON.stringify(errorData, null, 2),
    },
  };
};

const EnhancedErrorDisplay = ({ error }) => {
  const [opened, setOpened] = useState(null);
  const { classes: classesTechnicalDetail } = useStylesTechnicalDetail();
  const { classes: classesAccordion } = useStylesAccordion();
  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  const { userMessage, technicalDetails } = useMemo(
    () => parseError(error),
    [error],
  );

  return (
    <Box
      className={`${classesTechnicalDetail.technical} technical-error-display`}
    >
      <Text pb={theme.padding.baseEm} ta="center">
        {userMessage}
      </Text>
      <Text pb={theme.padding.baseEm} ta="center">
        If the problem persists, please{" "}
        <CustomAnchor
          text="contact support"
          href="mailto:support@vbase.com"
          componentClasses="link"
        />
        .
      </Text>

      {technicalDetails && (
        <Box className={`${classesTechnicalDetail.technicalBlock} accordion`}>
          <Accordion
            className={classesAccordion.accordion}
            value={opened}
            onChange={setOpened}
            variant="separated"
          >
            <Accordion.Item value="technical">
              <Accordion.Control
                chevron={opened === "technical" ? <IconMinus /> : <IconPlus />}
              >
                <Flex gap={theme.flex.gap.xs}>
                  <Text className="title">Technical Details</Text>
                  {opened === "technical" ? (
                    <Text className="show-hide">Hide</Text>
                  ) : (
                    <>
                      <Box className="show-hide-box"></Box>
                      <Text className="show-hide hide">Show</Text>
                    </>
                  )}
                </Flex>
              </Accordion.Control>
              <Accordion.Panel>
                <Box>
                  {technicalDetails.timestamp && (
                    <Flex
                      className="item error-item"
                      gap={theme.flex.gap.xxs}
                      justify={theme.flex.justify.between}
                      align={theme.flex.align.end}
                    >
                      <Text className="key">Timestamp:</Text>
                      <Box className="dots"></Box>
                      <Text component="b" className="value">
                        {technicalDetails.timestamp}
                      </Text>
                    </Flex>
                  )}

                  {technicalDetails.parameters &&
                    Object.keys(technicalDetails.parameters).length > 0 && (
                      <Box className="item error-item">
                        <Text className="key">Parameters:</Text>
                        <Code block>
                          {JSON.stringify(technicalDetails.parameters, null, 2)}
                        </Code>
                      </Box>
                    )}

                  {technicalDetails.message && (
                    <Box className="item error-item">
                      <Text className="key">Error Message:</Text>
                      <Code block>{technicalDetails.message}</Code>
                    </Box>
                  )}

                  <Box className="item">
                    <Text className="key">
                      Please include this information when contacting support.
                    </Text>
                  </Box>
                </Box>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Box>
      )}
    </Box>
  );
};

EnhancedErrorDisplay.propTypes = {
  error: PropTypes.oneOfType([
    // Structured format from backend
    PropTypes.shape({
      error: PropTypes.shape({
        user_message: PropTypes.string.isRequired,
        technical_details: PropTypes.shape({
          message: PropTypes.string,
          timestamp: PropTypes.string,
          parameters: PropTypes.object,
        }),
      }),
    }),
    // Fallback: string or any other format (for backward compatibility)
    PropTypes.string,
    PropTypes.object,
  ]).isRequired,
};

export default EnhancedErrorDisplay;
